import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { useIsMobile } from '../../hooks/useIsMobile';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Telescope, ChevronRight, ExternalLink, RefreshCw, Globe } from 'lucide-react';

const TAG_COLORS = {
  'investissement': 'bg-green-500/20 text-green-400',
  'fiscalité': 'bg-orange-500/20 text-orange-400',
  'marché locatif': 'bg-blue-500/20 text-blue-400',
  'législation': 'bg-purple-500/20 text-purple-400',
  'crédit': 'bg-yellow-500/20 text-yellow-400',
  'construction': 'bg-red-500/20 text-red-400',
  'marché': 'bg-cyan-500/20 text-cyan-400',
  'actualité': 'bg-gray-500/20 text-gray-400',
};

const VigieCard = () => {
  const isMobile = useIsMobile();
  const { api } = useAuth();
  const navigate = useNavigate();
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchLatestNews();
  }, []);

  const fetchLatestNews = async () => {
    try {
      setLoading(true);
      const response = await api.get('/news/latest?limit=4');
      setArticles(response.data.articles || []);
    } catch (error) {
      console.error('Error fetching news:', error);
      setArticles([]);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffHours = Math.floor((now - date) / (1000 * 60 * 60));
    
    if (diffHours < 1) return "À l'instant";
    if (diffHours < 24) return `Il y a ${diffHours}h`;
    
    const diffDays = Math.floor(diffHours / 24);
    if (diffDays === 1) return "Hier";
    if (diffDays < 7) return `Il y a ${diffDays} jours`;
    
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });
  };

  return (
    <Card className="bg-card/50 border-border" data-testid="vigie-card">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-lg flex items-center gap-2 min-w-0 flex-1">
            <Telescope className="w-5 h-5 text-primary flex-shrink-0" />
            <span className="truncate">La Vigie Immobilière</span>
          </CardTitle>
          <Button 
            variant="ghost" 
            size="sm"
            onClick={() => navigate('/vigie')}
            className="text-muted-foreground hover:text-primary flex-shrink-0 px-2"
          >
            <span className="hidden sm:inline">Voir tout</span>
            <ChevronRight className="w-4 h-4 sm:ml-1" />
          </Button>
        </div>
        <p className="text-xs text-muted-foreground truncate">Dernières actualités immobilières</p>
      </CardHeader>
      
      <CardContent className="space-y-3">
        {loading ? (
          // Loading skeleton
          [...Array(3)].map((_, i) => (
            <div key={i} className="animate-pulse space-y-2 p-3 bg-secondary/30 rounded-lg">
              <div className="h-4 bg-secondary rounded w-3/4" />
              <div className="h-3 bg-secondary rounded w-1/2" />
            </div>
          ))
        ) : articles.length === 0 ? (
          <div className="text-center py-6 text-muted-foreground">
            <Telescope className="w-8 h-8 mx-auto mb-2 opacity-50" />
            <p className="text-sm">Aucune actualité pour le moment</p>
            <Button 
              variant="outline" 
              size="sm" 
              className="mt-2"
              onClick={fetchLatestNews}
            >
              <RefreshCw className="w-4 h-4 mr-1" />
              Actualiser
            </Button>
          </div>
        ) : (
          <>
            {articles.slice(0, isMobile ? 2 : articles.length).map((article, index) => (
              <a
                key={article.article_id || index}
                href={article.url}
                target="_blank"
                rel="noopener noreferrer"
                className="block p-3 bg-secondary/30 rounded-lg hover:bg-secondary/50 transition-colors group"
              >
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <h4 className="font-medium text-sm text-foreground group-hover:text-primary transition-colors line-clamp-2">
                      {article.title}
                    </h4>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-muted-foreground flex items-center gap-1">
                        <Globe className="w-3 h-3" />
                        {article.source}
                      </span>
                      <span className="text-xs text-muted-foreground">•</span>
                      <span className="text-xs text-muted-foreground">
                        {formatDate(article.published_at)}
                      </span>
                    </div>
                    {article.tags && article.tags.length > 0 && (
                      <div className="flex gap-1 mt-2">
                        {article.tags.slice(0, 2).map(tag => (
                          <Badge 
                            key={tag}
                            variant="outline"
                            className={`text-[10px] px-1.5 py-0 capitalize ${TAG_COLORS[tag] || TAG_COLORS['actualité']}`}
                          >
                            {tag}
                          </Badge>
                        ))}
                      </div>
                    )}
                  </div>
                  <ExternalLink className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity flex-shrink-0" />
                </div>
              </a>
            ))}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default VigieCard;
